import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "../i18n";

/** Menú kebab (⋯) de acciones por fila — patrón de SuperAdminUsuariosPage,
 *  extraído como componente reutilizable (Mis Productos, Competidores…).
 *  Renderiza el dropdown en un portal para que no lo recorte el overflow
 *  de la tabla. */

export type RowAction = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
};

const ITEM_H = 38; // alto aprox. por item para decidir si abre hacia arriba

export default function RowActionsMenu({ actions, title }: { actions: RowAction[]; title?: string }) {
  const tr = useT();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        btnRef.current && !btnRef.current.contains(t)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function handleOpen() {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuH = actions.length * ITEM_H + 10;
      const openUpward = rect.bottom + menuH > window.innerHeight;
      setPos({
        top: openUpward ? rect.top - menuH - 4 : rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(v => !v);
  }

  const dropdown = open ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed", top: pos.top, right: pos.right,
        background: "var(--clr-modal-solid)",
        border: "1px solid var(--clr-border)",
        borderRadius: 12,
        boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
        zIndex: 9999, minWidth: 190, padding: "0.3rem",
      }}
    >
      {actions.map(a => (
        <button
          key={a.label}
          onClick={() => { setOpen(false); a.onClick(); }}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 9,
            padding: "0.55rem 0.875rem", background: "none", border: "none",
            cursor: "pointer", textAlign: "left", fontSize: "0.82rem",
            color: a.danger ? "#ef4444" : "var(--clr-text)",
            transition: "background 0.1s", borderRadius: 8,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = a.danger ? "rgba(239,68,68,0.08)" : "var(--clr-hover)")}
          onMouseLeave={e => (e.currentTarget.style.background = "none")}
        >
          {a.icon && <span style={{ opacity: a.danger ? 0.9 : 0.6, display: "flex" }}>{a.icon}</span>}
          {a.label}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ display: "inline-block" }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        title={title ?? tr("rowActions.actions")}
        style={{
          width: 32, height: 32, borderRadius: 12,
          border: "1px solid var(--clr-border)",
          background: open ? "var(--clr-hover)" : "transparent",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--clr-text3)", fontSize: "1.1rem", lineHeight: 1,
          transition: "all 0.1s",
        }}
      >
        ⋯
      </button>
      {dropdown}
    </div>
  );
}

/** Iconos de línea para las acciones más comunes */
export function IconTrash() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    </svg>
  );
}

export function IconOpen() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6M10 14 21 3" />
    </svg>
  );
}

/** Ojo — acción "activar / mostrar". */
export function IconEye() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Ojo tachado — acción "desactivar / ocultar". */
export function IconEyeOff() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
